import COLORS from '@/resources/style/Themes';
import { Box, SxProps, Theme } from '@mui/material';
import {
  DataGrid,
  GridColDef,
  GridValidRowModel,
  GridRowClassNameParams,
  GridSlots,
  GridSlotsComponentsProps,
  GridRowId,
  GridRowSelectionModel,
  GridRowParams,
} from '@mui/x-data-grid';
import { useMemo, useCallback } from 'react';

const HEADER_ICON_HOVER_BACKGROUND = 'rgba(255, 255, 255, 0.16)';

type NavigatorDataGridProps<T extends GridValidRowModel> = {
  columns: GridColDef<T>[];
  rows: T[];
  getRowId?: (row: T) => string | number;
  initialPageSize?: number;
  pageSizeOptions?: number[];
  showToolbar?: boolean;
  slots?: Partial<GridSlots>;
  slotProps?: GridSlotsComponentsProps;
  sx?: SxProps<Theme>;
  height?: string | number;
  getRowClassName?: (params: GridRowClassNameParams<T>) => string;
  checkboxSelection?: boolean;
  rowSelectionModel?: GridRowId[];
  onRowSelectionModelChange?: (newSelection: GridRowId[]) => void;
  onRowClick?: (params: GridRowParams<T>) => void;
  columnVisibilityModel?: Record<string, boolean>;
};

/**
 * DataGrid component with custom styling to match the Navigator Console design. It also has some custom behavior, such as auto row height and disabling row selection on click.
 * @param columns Columns for the DataGrid. Note, the first column must have a class "first"
 * in order to render properly (i.e. with left padding and rounded corners on the header). This is a bit of a hack, but it allows us to keep the styling consistent without having to worry about the specific column field names.
 * @param rows The data to display on the table
 * @param getRowId Optional function to get the unique ID for each row
 * @param initialPageSize Optional initial page size (default: 10)
 * @param pageSizeOptions Optional page size options (default: [10, 25, 50])
 * @param showToolbar Optional flag to show the toolbar (default: false)
 * @param slots Optional slots for custom components (e.g., toolbar)
 * @param slotProps Optional props for slot components
 * @param sx Optional custom styles to merge with default styles
 * @returns
 */
export const NavigatorDataGrid = <T extends GridValidRowModel>(
  props: NavigatorDataGridProps<T>,
) => {
  const {
    columns,
    rows,
    getRowId,
    initialPageSize = 10,
    pageSizeOptions = [10, 25, 50],
    showToolbar = false,
    slots,
    slotProps,
    sx,
    height,
    getRowClassName,
    checkboxSelection = false,
    rowSelectionModel,
    onRowSelectionModelChange,
    onRowClick,
    columnVisibilityModel,
  } = props;

  // Convert external GridRowId[] to internal GridRowSelectionModel for MUI X v8
  // Always provide a valid selection model to avoid uncontrolled behavior
  const internalRowSelectionModel: GridRowSelectionModel = useMemo(() => {
    const ids = rowSelectionModel ?? [];
    return { type: 'include' as const, ids: new Set(ids) };
  }, [rowSelectionModel]);

  // Handle selection change - convert from MUI X v8 format back to GridRowId[]
  const handleRowSelectionModelChange = useCallback(
    (newModel: GridRowSelectionModel) => {
      if (!onRowSelectionModelChange) return;

      // MUI X v8 uses { type: 'include' | 'exclude', ids: Set } format
      // 'include' means only those ids are selected
      // 'exclude' means all rows EXCEPT those ids are selected (requires row data to compute)
      if (newModel && newModel.ids instanceof Set) {
        if (newModel.type === 'include') {
          onRowSelectionModelChange(Array.from(newModel.ids));
        } else if (newModel.type === 'exclude') {
          // For 'exclude' type with empty set, it means ALL visible rows are selected
          // We need to pass the visible row ids - but we don't have access to them here
          // For now, we'll pass an empty array and let the parent handle select all differently
          // Or we can use the rows prop
          const allRowIds = rows.map((row) => {
            if (getRowId) {
              return getRowId(row);
            }
            return (row as { id?: GridRowId }).id ?? '';
          });
          const excludedIds = newModel.ids;
          const selectedIds = allRowIds.filter((id) => !excludedIds.has(id));
          onRowSelectionModelChange(selectedIds);
        }
      }
    },
    [onRowSelectionModelChange, rows, getRowId],
  );

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        height: height || 'auto',
        minHeight: height ? 0 : undefined,
        overflow: height ? 'hidden' : undefined,
      }}
    >
      <DataGrid
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        getRowId={getRowId}
        getRowClassName={getRowClassName}
        checkboxSelection={checkboxSelection}
        rowSelectionModel={internalRowSelectionModel}
        onRowSelectionModelChange={handleRowSelectionModelChange}
        onRowClick={onRowClick}
        sx={{
          ...(height
            ? {
                flex: 1,
                height: '100%',
                minHeight: 0,
                maxHeight: '100%',
                '& .MuiDataGrid-main': {
                  overflow: 'hidden',
                },
                '& .MuiDataGrid-footerContainer': {
                  flexShrink: 0,
                },
              }
            : {}),
          borderRadius: '8px',
          '& .MuiDataGrid-cell': {
            alignContent: 'center',
          },
          '& .MuiDataGrid-row:nth-of-type(even)': {
            backgroundColor: COLORS.TABLE_ROW_GRAY,
          },
          '& .MuiDataGrid-row': {
            paddingY: 1,
            ...(onRowClick ? { cursor: 'pointer' } : {}),
            '&:hover': {
              backgroundColor: COLORS.TABLE_HEADER_GRAY,
            },
          },
          '& .first': {
            paddingLeft: '2rem',
          },
          '& .MuiDataGrid-mainContent': {
            borderRadius: '8px',
          },
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell': {
            py: '8px',
          },
          '&.MuiDataGrid-root--densityStandard .MuiDataGrid-cell': {
            py: '15px',
          },
          '&.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell': {
            py: '22px',
          },
          '& .MuiDataGrid-columnHeaders .first': {
            borderTopLeftRadius: '8px',
          },
          '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader--last': {
            borderTopRightRadius: '8px',
          },
          '& .MuiDataGrid-columnHeader': {
            backgroundColor: COLORS.TABLE_HEADER_GRAY,
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-menuIcon': {
            opacity: 1,
            visibility: 'visible',
            width: 'auto',
          },
          '& .MuiDataGrid-menuIconButton': {
            opacity: 1,
            visibility: 'visible',
          },
          '& .MuiDataGrid-columnHeader:hover .MuiDataGrid-menuIcon': {
            opacity: 1,
            visibility: 'visible',
          },
          '& .MuiDataGrid-columnHeader .MuiCheckbox-root': {
            color: COLORS.WHITE,
          },
          '& .MuiDataGrid-columnHeader .MuiCheckbox-root.Mui-checked': {
            color: COLORS.WHITE,
          },
          '& .MuiDataGrid-menuIcon .MuiIconButton-root': {
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiDataGrid-columnHeader .MuiDataGrid-sortButton': {
            backgroundColor: 'transparent',
            color: COLORS.WHITE,
          },
          '& .MuiDataGrid-columnHeader .MuiIconButton-root:hover': {
            backgroundColor: HEADER_ICON_HOVER_BACKGROUND,
          },
          '& .MuiDataGrid-sortIcon': {
            color: `${COLORS.WHITE} !important`,
          },
          '& .MuiDataGrid-columnSeparator': {
            color: `${COLORS.WHITE} !important`,
          },
          '& ::-webkit-scrollbar': {
            width: '0.3rem',
            height: '0.3rem',
          },
          '& ::-webkit-scrollbar-track': {
            background: COLORS.WHITE,
          },
          '& ::-webkit-scrollbar-thumb': {
            background: '#d1d1d1',
            borderRadius: '6px',
          },
          '& ::-webkit-scrollbar-thumb:hover': {
            background: '#a0a0a0',
          },
          '&, [class^=MuiDataGrid]': { border: 'none' },
          ...sx,
        }}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: initialPageSize,
            },
          },
          columns: {
            columnVisibilityModel: columnVisibilityModel ?? {},
          },
        }}
        pageSizeOptions={pageSizeOptions}
        columns={columns}
        rows={rows}
        slots={slots}
        showToolbar={showToolbar}
        slotProps={slotProps}
      />
    </Box>
  );
};
